import { Modal } from "@/components/ui/Modal";
import { useTranslation } from "react-i18next";
import { getShopLogoUrl } from "@/utils/shopLogos";
import { shopClickUrl } from "@/lib/shopClickUrl";

interface ShopListingInfo {
  id: string;
  shopName: string;
  price: number | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  shopListings: ShopListingInfo[];
  listingPrice: number | null;
  conditionLabel?: string | null;
}

function getShopColor(name: string): string {
  const colors = [
    "bg-blue-100 text-blue-700",
    "bg-green-100 text-green-700",
    "bg-purple-100 text-purple-700",
    "bg-orange-100 text-orange-700",
    "bg-pink-100 text-pink-700",
    "bg-teal-100 text-teal-700",
  ];
  let hash = 0;
  for (const char of name) hash = char.charCodeAt(0) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export function ListingShopPricesModal({
  open,
  onClose,
  shopListings,
  listingPrice,
  conditionLabel,
}: Props) {
  const { t } = useTranslation();
  const sorted = [...shopListings].sort((a, b) => {
    if (a.price == null) return 1;
    if (b.price == null) return -1;
    return a.price - b.price;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("listings.shop_prices_modal_title")}
      size="sm"
    >
      <div className="space-y-4">
        {conditionLabel && (
          <p className="text-xs text-gray-600">
            {t("listings.shop_prices_modal_hint", { condition: conditionLabel })}
          </p>
        )}

        <div className="space-y-2">
          {sorted.map((shop) => {
            const isCheaperThanListing =
              shop.price != null &&
              listingPrice != null &&
              shop.price < listingPrice;
            return (
              <a
                key={shop.id}
                href={shopClickUrl(shop.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-lg border border-gray-100 p-2.5 transition-colors hover:bg-gray-50"
              >
                {getShopLogoUrl(shop.shopName) ? (
                  <img
                    src={getShopLogoUrl(shop.shopName)!}
                    alt={shop.shopName}
                    className="h-8 w-8 shrink-0 rounded-lg object-contain"
                  />
                ) : (
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${getShopColor(shop.shopName)}`}
                  >
                    {shop.shopName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {shop.shopName}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {shop.price != null
                      ? `${shop.price.toFixed(2)} €`
                      : "—"}
                  </p>
                  {isCheaperThanListing && (
                    <p className="text-[10px] text-gray-400">
                      {t("listings.shop_prices_modal_new_label")}
                    </p>
                  )}
                </div>
                <svg
                  className="h-4 w-4 shrink-0 text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
